import { useState, useMemo } from 'react';
import {
  ShieldCheck,
  ShieldX,
  RefreshCw,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Minus,
  TrendingUp,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import { SlidePanel } from '@/components/ui/slide-panel.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { formatLabel } from '@/lib/utils.js';
import type { ArtefactCitation } from '@/components/ui/portal-artefact-panel.js';
import {
  type DefensibilityCriterion,
  type DefensibilityScore,
  ORDERED_CRITERIA,
  CRITERION_LABELS,
  SCORE_META,
  defensibilityKeys,
} from '@/lib/defensibility.js';

// ── Types ──────────────────────────────────────────────────────────────

interface TfaDiagnosticEntry {
  panelistId: string;
  assessment: 'strong' | 'adequate' | 'needs_work' | 'insufficient';
  issues: string[];
  suggestion: string;
}

interface MechanicalStats {
  activeWeekCoverage: number;
  atoCategoryCount: number;
  maxGapDays: number;
  nonGitRatio: number;
  timeEntryCoverage: number;
  compositeScore: number;
}

interface DefensibilityCheck {
  id: string;
  criterion: DefensibilityCriterion;
  score: DefensibilityScore;
  rationale: string;
  gapsSummary: string | null;
  suggestedActions: string[];
  supportingArtefactIds: string[];
  supportingCitations?: ArtefactCitation[];
  tfaDiagnostic: TfaDiagnosticEntry[] | null;
  mechanicalStats: MechanicalStats | null;
  categoryMap: Record<string, boolean> | null;
  isStale: boolean;
  refreshedAt: string;
}

interface SupportingArtefact {
  id: string;
  title: string;
  type: string;
  // Authoritative date the evidence CONTENT pertains to (git commit / meeting date), distinct
  // from createdAt (Edison ingestion date). Contemporaneity is what the ATO cares about, so
  // consumers display sourceDate ?? createdAt.
  sourceDate?: string | null;
  createdAt: string;
}

// GET /api/projects/:id/defensibility/history — one row per historical reading
// (append-only; `defensibility_check_history`), ordered oldest→newest by the API.
interface DefensibilityHistoryItem {
  id: string;
  criterion: DefensibilityCriterion;
  score: DefensibilityScore;
  rationale: string;
  gapsSummary: string | null;
  promptVersion: string;
  model: string;
  supportingArtefactCount: number;
  createdAt: string;
}

// ── Display config ─────────────────────────────────────────────────────

// Criterion descriptions are widget-specific (the shared module owns names/order); descriptions
// stay local. The name comes from the shared CRITERION_LABELS.
const CRITERION_DESCRIPTIONS: Record<DefensibilityCriterion, string> = {
  systematic_progression: 'Evidence of a systematic, investigative and experimental process',
  technical_uncertainty: 'The outcome could not have been known in advance',
  new_knowledge: 'Generation of new or improved technical knowledge',
  contemporaneous_documentation: 'Evidence documented contemporaneously throughout the project',
};

// Widget-specific card accents (left border + text colour). Label / icon / badge variant come
// from the shared SCORE_META so the widget, evidence log and dashboard stay in lockstep.
const SCORE_ACCENT: Record<DefensibilityScore, { borderClass: string; textClass: string }> = {
  strong: { borderClass: 'border-l-emerald-500', textClass: 'text-emerald-700 dark:text-emerald-400' },
  moderate: { borderClass: 'border-l-amber-500', textClass: 'text-amber-700 dark:text-amber-400' },
  weak: { borderClass: 'border-l-orange-500', textClass: 'text-orange-700 dark:text-orange-400' },
  gap: { borderClass: 'border-l-destructive', textClass: 'text-destructive' },
};

// ── Evidence slide panel ────────────────────────────────────────────────

/** Truncates a citation quote to a compact one-line preview. */
function truncateQuote(quote: string, maxLength: number): string {
  if (quote.length <= maxLength) return quote;
  return `${quote.slice(0, maxLength).trimEnd()}…`;
}

function MechanicalStatsSection({ stats }: { stats: MechanicalStats }) {
  const pct = (v: number) => `${(v * 100).toFixed(0)}%`;
  const rows: { label: string; value: string }[] = [
    { label: 'Active week coverage', value: pct(stats.activeWeekCoverage) },
    { label: 'AusIndustry category breadth', value: `${stats.atoCategoryCount}/7` },
    { label: 'Longest gap', value: `${Math.round(stats.maxGapDays)} days` },
    { label: 'Non-git artefact ratio', value: pct(stats.nonGitRatio) },
    { label: 'Time entry coverage', value: pct(stats.timeEntryCoverage) },
    { label: 'Composite score', value: `${(stats.compositeScore * 100).toFixed(0)}/100` },
  ];
  return (
    <div className="rounded-lg border bg-muted/20 p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
        Documentation Metrics
      </p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{r.label}</span>
            <span className="font-mono font-medium tabular-nums">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TfaDiagnosticSection({ entries }: { entries: TfaDiagnosticEntry[] }) {
  const assessmentBadge = (a: TfaDiagnosticEntry['assessment']) => {
    if (a === 'strong') return <Badge variant="positive">Strong</Badge>;
    if (a === 'adequate') return <Badge variant="default">Adequate</Badge>;
    if (a === 'needs_work') return <Badge variant="warning">Needs work</Badge>;
    return <Badge variant="destructive">Insufficient</Badge>;
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        TFA Panel Diagnostic
      </p>
      {entries.map((entry, i) => (
        <div key={i} className="rounded-lg border p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-mono text-muted-foreground truncate">
              Panelist {entry.panelistId.slice(0, 8)}…
            </span>
            {assessmentBadge(entry.assessment)}
          </div>
          {entry.issues.length > 0 && (
            <ul className="space-y-1">
              {entry.issues.map((issue, j) => (
                <li key={j} className="text-xs text-muted-foreground flex items-start gap-1.5">
                  <AlertCircle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />
                  {issue}
                </li>
              ))}
            </ul>
          )}
          {entry.suggestion && (
            <p className="text-xs text-muted-foreground border-t pt-2 mt-2">{entry.suggestion}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function EvidenceSlidePanel({
  projectId,
  check,
  onClose,
}: {
  projectId: string;
  check: DefensibilityCheck;
  onClose: () => void;
}) {
  const meta = SCORE_META[check.score];
  const ScoreIcon = meta.Icon;
  const accent = SCORE_ACCENT[check.score];
  const criterionName = CRITERION_LABELS[check.criterion];

  // Shares the evidence cache entry with the claim readiness evidence log via defensibilityKeys.
  // The queryFn unwraps to the inner { check, artefacts } object so both consumers store an
  // identical shape under the identical key.
  const { data, isLoading } = useQuery({
    queryKey: defensibilityKeys.evidence(projectId, check.criterion),
    queryFn: async () => {
      const res = await customInstance<{ data: { check: DefensibilityCheck | null; artefacts: SupportingArtefact[] } }>({
        url: `/api/projects/${projectId}/defensibility/${check.criterion}/evidence`,
        method: 'GET',
      });
      return res.data;
    },
  });

  const artefacts = data?.artefacts ?? [];

  // Prefer the freshly-fetched check's citations (data.check) over the possibly-stale `check`
  // prop, so a refresh picks up newly-verified citations without a full page reload.
  const citationsByArtefactId = new Map<string, ArtefactCitation>(
    (data?.check?.supportingCitations ?? check.supportingCitations ?? []).map((c) => [c.artefactId, c])
  );

  return (
    <SlidePanel
      open
      title={criterionName}
      onClose={onClose}
      width="w-[600px]"
    >
      <div className="p-5 space-y-6">
        {/* Score badge */}
        <div className="flex items-center gap-3">
          <ScoreIcon className={`h-4 w-4 shrink-0 ${accent.textClass}`} aria-hidden="true" />
          <div>
            <Badge variant={meta.badgeVariant} className="text-xs">
              {meta.label}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">{CRITERION_DESCRIPTIONS[check.criterion]}</p>
          </div>
        </div>

        {/* Rationale */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Assessment
          </p>
          <p className="text-sm text-foreground leading-relaxed">{check.rationale}</p>
        </div>

        {/* Gaps summary */}
        {check.gapsSummary && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20 p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400 mb-2">
              Evidence Gaps
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300">{check.gapsSummary}</p>
          </div>
        )}

        {/* Suggested actions */}
        {check.suggestedActions.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Suggested Actions
            </p>
            <ul className="space-y-1.5">
              {check.suggestedActions.map((action, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Minus className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
                  <span>{action}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* TFA diagnostic — C2 only */}
        {check.criterion === 'technical_uncertainty' &&
          check.tfaDiagnostic &&
          check.tfaDiagnostic.length > 0 && (
            <TfaDiagnosticSection entries={check.tfaDiagnostic} />
          )}

        {/* Mechanical stats — C4 only */}
        {check.criterion === 'contemporaneous_documentation' && check.mechanicalStats && (
          <MechanicalStatsSection stats={check.mechanicalStats} />
        )}

        {/* Category map — C4 only */}
        {check.criterion === 'contemporaneous_documentation' && check.categoryMap && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              AusIndustry Documentation Categories
            </p>
            <div className="grid grid-cols-1 gap-1.5">
              {Object.entries(check.categoryMap).map(([cat, covered]) => (
                <div key={cat} className="flex items-center gap-2 text-xs">
                  {covered ? (
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" aria-hidden="true" />
                  ) : (
                    <ShieldX className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" aria-hidden="true" />
                  )}
                  <span className={covered ? 'text-foreground' : 'text-muted-foreground'}>
                    {formatLabel(cat)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Supporting artefacts */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            Supporting Artefacts
          </p>
          {isLoading ? (
            <div className="space-y-2">
              <div className="h-8 rounded skeleton" />
              <div className="h-8 rounded skeleton" />
              <div className="h-8 rounded skeleton" />
            </div>
          ) : artefacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No supporting artefacts identified for this criterion.</p>
          ) : (
            <ul className="space-y-1.5">
              {artefacts.map((artefact) => {
                const citation = citationsByArtefactId.get(artefact.id);
                return (
                  <li key={artefact.id} className="rounded-lg border px-3 py-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="truncate flex-1 min-w-0 text-foreground">{artefact.title}</span>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-[11px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">
                          {formatLabel(artefact.type)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(artefact.sourceDate ?? artefact.createdAt).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      </div>
                    </div>
                    {citation && (
                      <p className="text-xs text-muted-foreground italic mt-1.5 border-l-2 border-muted pl-2">
                        &ldquo;{truncateQuote(citation.quote, 160)}&rdquo; · line {citation.line}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Last refreshed */}
        <p className="text-xs text-muted-foreground border-t pt-4">
          Last assessed{' '}
          {new Date(check.refreshedAt).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
        </p>
      </div>
    </SlidePanel>
  );
}

// ── Progression view (per-criterion history) — ERA-027 ─────────────────

function formatHistoryDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function CriterionProgressionSection({
  criterion,
  items,
}: {
  criterion: DefensibilityCriterion;
  items: DefensibilityHistoryItem[];
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-lg border">
      <button
        type="button"
        data-testid={`progression-toggle-${criterion}`}
        className="w-full flex items-center justify-between gap-2 p-3 text-left hover:bg-accent/50 transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <span className="text-sm font-medium text-foreground">
          {CRITERION_LABELS[criterion]}
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground">
            {items.length} reading{items.length !== 1 ? 's' : ''}
          </span>
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </button>

      {expanded && (
        <ul className="border-t divide-y">
          {items.map((item) => {
            const meta = SCORE_META[item.score];
            const accent = SCORE_ACCENT[item.score];
            const ScoreIcon = meta.Icon;
            return (
              <li key={item.id} className="p-3 flex items-start gap-2.5">
                <ScoreIcon className={`h-4 w-4 shrink-0 mt-0.5 ${accent.textClass}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold ${accent.textClass}`}>
                      {meta.label}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatHistoryDate(item.createdAt)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {item.rationale}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground/70">
                    <span className="font-mono">{item.promptVersion}</span>
                    <span>
                      {item.supportingArtefactCount} artefact
                      {item.supportingArtefactCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function DefensibilityProgressionView({ projectId }: { projectId: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['defensibility-history', projectId],
    queryFn: () =>
      customInstance<{ data: { items: DefensibilityHistoryItem[] } }>({
        url: `/api/projects/${projectId}/defensibility/history`,
        method: 'GET',
      }),
    enabled: !!projectId,
  });

  const items = data?.data?.items ?? [];

  // Display-only grouping of already-fetched, already-ordered (oldest→newest,
  // per the API) rows — not a substitute for SQL filtering/sorting/pagination.
  const grouped = useMemo(() => {
    const map = new Map<DefensibilityCriterion, DefensibilityHistoryItem[]>();
    for (const item of items) {
      const existing = map.get(item.criterion);
      if (existing) {
        existing.push(item);
      } else {
        map.set(item.criterion, [item]);
      }
    }
    return map;
  }, [items]);

  if (isLoading) {
    return (
      <div data-testid="defensibility-progression" className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-11 rounded-lg skeleton" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="defensibility-progression"
        className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load progression history.</span>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p data-testid="defensibility-progression" className="text-sm text-muted-foreground text-center py-4">
        No prior readings yet.
      </p>
    );
  }

  return (
    <div data-testid="defensibility-progression" className="space-y-2">
      {ORDERED_CRITERIA.map((criterion) => {
        const criterionItems = grouped.get(criterion) ?? [];
        if (criterionItems.length === 0) return null;
        return (
          <CriterionProgressionSection key={criterion} criterion={criterion} items={criterionItems} />
        );
      })}
    </div>
  );
}

// ── Main widget ────────────────────────────────────────────────────────

export function DefensibilityWidget({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const [selectedCheck, setSelectedCheck] = useState<DefensibilityCheck | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showProgression, setShowProgression] = useState(false);

  // Shared with the claim readiness evidence log via defensibilityKeys — one cache entry, so a
  // "Refresh All" here invalidates the evidence log too (and vice versa). The queryFn unwraps to
  // the inner { checks } object so both consumers store an identical shape under this key.
  const QUERY_KEY = defensibilityKeys.checks(projectId);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await customInstance<{ data: { checks: DefensibilityCheck[] } }>({
        url: `/api/projects/${projectId}/defensibility`,
        method: 'GET',
      });
      return res.data;
    },
    enabled: !!projectId,
  });

  const checks = data?.checks ?? [];

  // Map to lookup by criterion for display ordering
  const checkMap = new Map<DefensibilityCriterion, DefensibilityCheck>(
    checks.map((c) => [c.criterion, c]),
  );

  const hasAny = checks.length > 0;
  const hasStale = checks.some((c) => c.isStale);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await customInstance({
        url: `/api/projects/${projectId}/defensibility/refresh`,
        method: 'POST',
      });
      // Route runs checks synchronously — invalidate immediately on success.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    } catch {
      // Ignore — checks may still have been saved (API Gateway timeout on slow projects).
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    } finally {
      setIsRefreshing(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="rounded-lg border p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="h-5 w-40 rounded skeleton" />
          <div className="h-8 w-28 rounded skeleton" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-lg skeleton" />
          ))}
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>Failed to load defensibility checks.</span>
        </div>
        <Button variant="secondary" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────────
  if (!hasAny) {
    return (
      <div className="rounded-lg border p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              AusIndustry Defensibility
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              4-criterion readiness assessment against Division 355 eligibility gates
            </p>
          </div>
        </div>
        <div className="rounded-lg border border-dashed p-8 text-center">
          <ShieldCheck className="h-8 w-8 mx-auto text-muted-foreground/40" aria-hidden="true" />
          <p className="mt-3 text-sm text-muted-foreground">No checks run yet.</p>
          <p className="text-xs text-muted-foreground mt-1">
            Run the check to assess your project's AusIndustry audit readiness across 4 key criteria.
          </p>
          <Button
            className="mt-4"
            onClick={() => void handleRefresh()}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                Running check… (~25s)
              </>
            ) : (
              <>
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Run Check
              </>
            )}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground/70 text-center leading-relaxed">
          Readiness indicator only — not legal advice. Review with your R&D tax adviser before lodging.
        </p>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────
  return (
    <>
      <div className="rounded-lg border p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2 text-sm">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              AusIndustry Defensibility
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Division 355 readiness across 4 eligibility criteria
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => void handleRefresh()}
            disabled={isRefreshing}
            aria-label={isRefreshing ? 'Refreshing checks…' : 'Refresh all defensibility checks'}
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            {isRefreshing ? 'Running… (~25s)' : 'Refresh All'}
          </Button>
        </div>

        {/* Staleness banner */}
        {hasStale && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              One or more checks are stale — evidence has changed since the last run. Refresh to update.
            </span>
          </div>
        )}

        {/* 2×2 grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {ORDERED_CRITERIA.map((criterion) => {
            const check = checkMap.get(criterion);

            // Placeholder card if criterion hasn't been run yet
            if (!check) {
              return (
                <div
                  key={criterion}
                  className="rounded-lg border border-l-4 border-l-muted-foreground/30 p-4 opacity-50"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground mb-1">
                        <Minus className="h-4 w-4 shrink-0" aria-hidden="true" />
                        Not run
                      </div>
                      <p className="text-sm font-medium text-muted-foreground">
                        {CRITERION_LABELS[criterion]}
                      </p>
                      <p className="text-xs text-muted-foreground/60 mt-0.5">—</p>
                    </div>
                  </div>
                </div>
              );
            }

            const meta = SCORE_META[check.score];
            const ScoreIcon = meta.Icon;
            const accent = SCORE_ACCENT[check.score];

            return (
              <div
                key={criterion}
                className={`rounded-lg border border-l-4 ${accent.borderClass} p-4 cursor-pointer hover:bg-accent/50 transition-colors`}
                onClick={() => setSelectedCheck(check)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedCheck(check);
                  }
                }}
                aria-label={`${CRITERION_LABELS[criterion]}: ${meta.label}. Click to view details.`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className={`flex items-center gap-1.5 text-xs font-semibold ${accent.textClass} mb-1`}>
                      <ScoreIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
                      {meta.label}
                      {check.isStale && (
                        <span className="text-amber-500 font-normal ml-1">· stale</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-foreground leading-snug">
                      {CRITERION_LABELS[criterion]}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {check.supportingArtefactIds.length > 0
                        ? `${check.supportingArtefactIds.length} artefact${check.supportingArtefactIds.length !== 1 ? 's' : ''}`
                        : '0 artefacts'}
                    </p>
                  </div>
                  <ChevronRight
                    className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5"
                    aria-hidden="true"
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* Progression — per-criterion score history over time */}
        <div className="border-t pt-3">
          <button
            type="button"
            className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowProgression((prev) => !prev)}
            aria-expanded={showProgression}
          >
            <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
            {showProgression ? 'Hide' : 'Show'} score progression
            {showProgression ? (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
            )}
          </button>
          {showProgression && (
            <div className="mt-3">
              <DefensibilityProgressionView projectId={projectId} />
            </div>
          )}
        </div>

        {/* Footer disclaimer */}
        <p className="text-[11px] text-muted-foreground/70 text-center leading-relaxed border-t pt-3">
          Readiness indicator only — not legal advice. Review with your R&D tax adviser before lodging.
        </p>
      </div>

      {/* Evidence slide panel — lazy fetches only when opened */}
      {selectedCheck && (
        <EvidenceSlidePanel
          projectId={projectId}
          check={selectedCheck}
          onClose={() => setSelectedCheck(null)}
        />
      )}
    </>
  );
}
